import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtMoney, fmtCompact, fmtPct, fmtDate } from "@/lib/format";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  FileText,
  Wallet,
  Receipt,
  AlertCircle,
  RefreshCw,
  ArrowUpRight,
  Landmark,
} from "lucide-react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
} from "recharts";
import { toast } from "sonner";

const KpiCell = ({ label, value, sub, icon: Icon, accent, testId, to, color = "#1A2332", isLast }) => {
  const content = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-gray-500">
          {label}
        </div>
        {Icon && (
          <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: `${color}15` }}>
            <Icon className="w-4 h-4" style={{ color }} strokeWidth={2} />
          </div>
        )}
      </div>
      <div>
        <div className="text-[26px] md:text-[28px] font-semibold tracking-tight leading-none mt-2" style={{ color }}>
          {value}
        </div>
        {sub && (
          <div className={`text-xs mt-2 ${accent === "neg" ? "text-red-600" : accent === "pos" ? "text-emerald-700" : "text-gray-500"}`}>
            {sub}
          </div>
        )}
        {to && (
          <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 mt-3 flex items-center gap-1 group-hover:text-[#1A2332] transition-colors">
            View detail <ArrowUpRight className="w-3 h-3" />
          </div>
        )}
      </div>
    </>
  );

  const classes = `group p-5 flex flex-col justify-between min-h-[140px] transition-colors hover:bg-gray-50/70 ${
    !isLast ? "lg:border-r border-gray-100" : ""
  }`;

  if (to) {
    return (
      <Link to={to} data-testid={testId} className={`${classes} cursor-pointer`}>
        {content}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className={classes}>
      {content}
    </div>
  );
};

const SectionGroup = ({ title, subtitle, cols = 4, children, testId }) => {
  const colsClass =
    cols === 3
      ? "grid-cols-1 md:grid-cols-3"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
  return (
    <section
      data-testid={testId}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-600">
          {title}
        </div>
        {subtitle && (
          <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 hidden sm:block">
            {subtitle}
          </div>
        )}
      </div>
      <div className={`grid ${colsClass} divide-y md:divide-y-0 divide-gray-100`}>
        {children}
      </div>
    </section>
  );
};

const SectionTitle = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 mb-3">
    {children}
  </div>
);

const periodLabel = (p) => {
  if (p === "ytd") return "Year to date";
  if (p === "mtd") return "Month to date";
  return "Inception till date";
};

const Dashboard = () => {
  const { email } = useAuth();
  const [m, setM] = useState(null);
  const [series, setSeries] = useState([]);
  const [costSummary, setCostSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [zohoConnected, setZohoConnected] = useState(false);
  const [period, setPeriod] = useState(() => {
    try {
      return localStorage.getItem("dashboard:period") || "itd";
    } catch {
      return "itd";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("dashboard:period", period);
    } catch {
      /* ignore */
    }
  }, [period]);

  const load = async () => {
    setLoading(true);
    try {
      const [metricsR, monthlyR, costR, statusR] = await Promise.all([
        api.get(`/metrics?period=${period}`),
        api.get("/metrics/monthly"),
        api.get(`/costs/summary?period=${period}`),
        api.get("/zoho/status"),
      ]);
      setM(metricsR.data);
      setSeries(monthlyR.data.series || []);
      setCostSummary(costR.data.summary || []);
      setZohoConnected(statusR.data.connected);
    } catch (e) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [period]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/zoho/sync");
      toast.success(`Synced: ${r.data.counts.invoices} invoices · ${r.data.counts.salesorders} POs`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const displayName = (email || "").split("@")[0].replace(/[._-]/g, " ").trim() || "there";

  return (
    <div className="p-5 md:p-7 max-w-[1600px] mx-auto space-y-6">
      {/* Page meta row */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500">
            Operational Overview · {m?.base_currency || "USD"}
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#1A2332] mt-1">
            Dashboard
          </h1>
          <div className="text-xs text-gray-400 mt-1" data-testid="last-synced">
            {m?.synced_at ? `Last synced ${fmtDate(m.synced_at)} · ${new Date(m.synced_at).toLocaleTimeString()}` : "No data synced yet"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* Period filter */}
          <div
            data-testid="period-filter"
            className="inline-flex bg-white border border-gray-200 rounded-md p-0.5 shadow-sm"
            role="tablist"
            aria-label="Date range"
          >
            {[
              { key: "itd", label: "Inception" },
              { key: "ytd", label: "YTD" },
              { key: "mtd", label: "MTD" },
            ].map((opt) => (
              <button
                key={opt.key}
                role="tab"
                aria-selected={period === opt.key}
                onClick={() => setPeriod(opt.key)}
                data-testid={`period-${opt.key}`}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  period === opt.key
                    ? "bg-[#1A2332] text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {!zohoConnected && (
            <Link
              to="/settings"
              data-testid="connect-zoho-cta"
              className="border border-gray-300 px-3.5 py-2 rounded-md text-sm font-medium text-[#1A2332] hover:bg-gray-50 transition-colors flex items-center gap-1.5"
            >
              Connect Zoho <ArrowUpRight className="w-4 h-4" />
            </Link>
          )}
          <button
            onClick={handleSync}
            disabled={syncing || !zohoConnected}
            data-testid="sync-zoho-button"
            className="text-white px-4 py-2 rounded-md font-medium text-sm hover:opacity-90 transition-all flex items-center gap-2 disabled:opacity-50"
            style={{ background: "#408DFB" }}
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Sync Zoho"}
          </button>
        </div>
      </div>

      {!zohoConnected && (
        <div
          data-testid="zoho-banner"
          className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sm text-amber-900">Zoho Books is not connected</div>
            <div className="text-sm text-amber-800 mt-1">
              Connect your Zoho Books account to pull contracts, invoices and payments.{" "}
              <Link to="/settings" className="underline font-medium">Go to Settings →</Link>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Snapshot */}
      <SectionGroup title="Revenue Snapshot" subtitle={`From Zoho Books · ${periodLabel(period)}`} cols={4} testId="section-revenue">
        <KpiCell label="Total Contract Value" value={fmtCompact(m?.total_contract_value, "USD")} sub={`${m?.counts?.salesorders ?? 0} sales orders`} icon={FileText} testId="kpi-contract-value" to="/details/contract-value" color="#1E3A8A" />
        <KpiCell label="Total Billed" value={fmtCompact(m?.total_billed, "USD")} sub={`${m?.counts?.invoices ?? 0} invoices`} icon={Receipt} testId="kpi-billed" to="/details/billed" color="#408DFB" />
        <KpiCell label="Balance to Bill" value={fmtCompact(m?.balance_to_bill, "USD")} sub="Contract − Billed" icon={TrendingUp} testId="kpi-balance-to-bill" to="/details/balance-to-bill" color="#B8860B" />
        <KpiCell label="Outstanding Receivables" value={fmtCompact(m?.outstanding_receivables, "USD")} sub="Invoices raised but not collected" icon={AlertCircle} accent="neg" testId="kpi-outstanding" to="/details/outstanding" color="#DC2626" isLast />
      </SectionGroup>

      {/* Tax row */}
      <SectionGroup title="Tax (FTA / VAT)" subtitle={`Owed to authority — not your revenue · ${periodLabel(period)}`} cols={3} testId="section-tax">
        <KpiCell label="Tax Billed" value={fmtCompact(m?.tax_billed, "USD")} sub="VAT/GST charged on invoices" icon={Landmark} testId="kpi-tax-billed" to="/details/tax" color="#B8860B" />
        <KpiCell label="Tax Collected" value={fmtCompact(m?.tax_collected, "USD")} sub="Portion of receipts that is tax" icon={Landmark} testId="kpi-tax-collected" to="/details/tax" color="#047857" />
        <KpiCell label="Tax Outstanding" value={fmtCompact(m?.tax_outstanding, "USD")} sub="Tax billed minus tax collected" icon={Landmark} accent={m?.tax_outstanding > 0 ? "neg" : undefined} testId="kpi-tax-outstanding" to="/details/tax" color={m?.tax_outstanding > 0 ? "#DC2626" : "#B8860B"} isLast />
      </SectionGroup>

      {/* Profitability */}
      <SectionGroup title="Profitability" subtitle={`Net of tax · ${periodLabel(period)}`} cols={4} testId="section-profitability">
        <KpiCell label="Revenue Collected" value={fmtCompact(m?.revenue_collected, "USD")} sub={`Net of tax: ${fmtCompact(m?.net_revenue, "USD")}`} icon={TrendingUp} accent="pos" testId="kpi-revenue" to="/details/revenue" color="#047857" />
        <KpiCell label="Total Costs" value={fmtCompact(m?.total_costs, "USD")} sub={`MTD: ${fmtCompact(m?.current_month_costs, "USD")}`} icon={Wallet} testId="kpi-total-costs" to="/details/total-costs" color="#B8860B" />
        <KpiCell label="Gross Margin" value={fmtCompact(m?.gross_margin, "USD")} sub={`${fmtPct(m?.gross_margin_pct)} · Net Rev − Direct`} icon={m?.gross_margin >= 0 ? TrendingUp : TrendingDown} accent={m?.gross_margin >= 0 ? "pos" : "neg"} testId="kpi-gross-margin" to="/details/gross-margin" color={m?.gross_margin >= 0 ? "#047857" : "#DC2626"} />
        <KpiCell label="Net Margin" value={fmtCompact(m?.net_margin, "USD")} sub={`${fmtPct(m?.margin_pct)} · Net Rev − All Costs`} icon={m?.net_margin >= 0 ? TrendingUp : TrendingDown} accent={m?.net_margin >= 0 ? "pos" : "neg"} testId="kpi-net-margin" to="/details/net-margin" color={m?.net_margin >= 0 ? "#047857" : "#DC2626"} isLast />
      </SectionGroup>

      {/* Charts grouped */}
      <section
        data-testid="section-charts"
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-600">
            Trends &amp; Breakdown
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 hidden sm:block">
            Trailing 12 months
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
          <div className="lg:col-span-2 p-5" data-testid="revenue-cost-chart">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-gray-500">Revenue vs Costs</div>
                <h3 className="text-lg font-semibold tracking-tight text-[#1A2332] mt-1">Monthly Activity</h3>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={series} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#F1F2F4" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="month" stroke="#9CA3AF" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                <YAxis stroke="#9CA3AF" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1A2332", border: "none", borderRadius: 6, fontSize: 12, color: "#FFF" }}
                  labelStyle={{ color: "#FFF" }}
                  itemStyle={{ color: "#FFF" }}
                  formatter={(v) => `$${Number(v).toLocaleString()}`}
                />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }} />
                <Bar dataKey="revenue" fill="#408DFB" name="Revenue" radius={[3,3,0,0]} />
                <Bar dataKey="costs" fill="#B8860B" name="Costs" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="p-5" data-testid="cost-breakdown">
            <div className="text-xs uppercase tracking-[0.15em] text-gray-500">By Cost Head</div>
            <h3 className="text-lg font-semibold tracking-tight text-[#1A2332] mt-1 mb-4">Cost Breakdown</h3>
            <div className="space-y-3">
              {costSummary.length === 0 || costSummary.every((c) => c.total_usd === 0) ? (
                <div className="text-sm text-gray-500 py-8 text-center">
                  No costs entered yet.{" "}
                  <Link to="/costs" className="underline font-medium text-[#1A2332]">Add costs →</Link>
                </div>
              ) : (
                costSummary.map((c) => {
                  const max = Math.max(...costSummary.map((x) => x.total_usd), 1);
                  const w = (c.total_usd / max) * 100;
                  return (
                    <div key={c.head} className="space-y-1.5">
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs text-gray-700 font-medium truncate pr-2">{c.head}</span>
                        <span className="text-xs font-medium text-[#1A2332]">{fmtMoney(c.total_usd, "USD")}</span>
                      </div>
                      <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${w}%`, background: "#408DFB" }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Margin trend */}
      {series.length > 0 && (
        <section
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          data-testid="margin-trend"
        >
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-600">
              Margin Trend
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400 hidden sm:block">
              Net · Trailing 12 months
            </div>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#F1F2F4" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="month" stroke="#9CA3AF" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#E5E7EB" }} />
                <YAxis stroke="#9CA3AF" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1A2332", border: "none", borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: "#FFF" }}
                  itemStyle={{ color: "#FFF" }}
                  formatter={(v) => `$${Number(v).toLocaleString()}`}
                />
                <Line type="monotone" dataKey="margin" stroke="#047857" strokeWidth={2.5} dot={{ fill: "#047857", r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
    </div>
  );
};

export default Dashboard;
